import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, GraduationCap, Tag, ShoppingCart } from "lucide-react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EnquireButton } from "@/components/EnquireButton";

function Price({ c }) {
  const hasDiscount = c.discount_price != null && c.discount_price < c.price;
  if (c.plan_type === "free" || !c.price) return <div className="font-display text-2xl font-black text-emerald-600">Free</div>;
  if (hasDiscount) {
    return (
      <div className="flex items-end gap-2">
        <div className="font-display text-2xl font-black">₹{c.discount_price.toLocaleString("en-IN")}</div>
        <div className="pb-0.5 text-sm text-muted-foreground line-through">₹{c.price.toLocaleString("en-IN")}</div>
      </div>
    );
  }
  return <div className="font-display text-2xl font-black">₹{c.price.toLocaleString("en-IN")}</div>;
}

export function CourseCard({ c }) {
  const hasDiscount = c.discount_price != null && c.discount_price < c.price;
  const pct = hasDiscount ? Math.round(((c.price - c.discount_price) / c.price) * 100) : 0;
  const canBuy = c.buy_enabled !== false;
  return (
    <Card className="group flex flex-col overflow-hidden border-border/70 transition hover:-translate-y-1 hover:shadow-lg" data-testid={`course-card-${c.slug}`}>
      <Link to={`/courses/${c.slug}`} className="brand-gradient relative flex h-36 items-center justify-center">
        {c.image_url ? <img src={c.image_url} alt={c.name} className="h-full w-full object-cover" /> : <GraduationCap className="h-14 w-14 text-white/80" />}
        {hasDiscount && <div className="absolute right-3 top-3 rounded-full bg-[hsl(var(--brand-orange))] px-2.5 py-0.5 text-[11px] font-bold text-white">-{pct}%</div>}
        {c.plan_type === "free" && <div className="absolute left-3 top-3 rounded-full bg-emerald-500 px-2.5 py-0.5 text-[11px] font-bold text-white">FREE</div>}
      </Link>
      <CardContent className="flex flex-1 flex-col p-5">
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{c.category || "Course"}</div>
        <Link to={`/courses/${c.slug}`} className="mt-1 font-display text-lg font-bold leading-snug hover:underline">{c.name}</Link>
        <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{c.short_description}</p>
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          {c.duration && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {c.duration}</span>}
          {c.course_type && <span className="inline-flex items-center gap-1 capitalize"><Tag className="h-3.5 w-3.5" /> {c.course_type}</span>}
        </div>
        <div className="mt-auto pt-5">
          <Price c={c} />
          <div className="mt-4 grid grid-cols-2 gap-2">
            {canBuy ? (
              <Button asChild className="bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90" data-testid={`buy-now-${c.slug}`}>
                <Link to={`/checkout/${c.slug}`}><ShoppingCart className="mr-1.5 h-4 w-4" /> {c.plan_type === "free" || !c.price ? "Enroll Free" : "Buy Now"}</Link>
              </Button>
            ) : (
              <Button disabled variant="secondary" data-testid={`buy-closed-${c.slug}`}>Enrollment closed</Button>
            )}
            <EnquireButton course={c.name} testId={`enquire-${c.slug}`} label="Enquire" />
          </div>
          <Link to={`/courses/${c.slug}`} className="mt-3 block text-center text-xs font-semibold text-[hsl(var(--brand-navy))] hover:underline" data-testid={`course-cta-${c.slug}`}>View details →</Link>
        </div>
      </CardContent>
    </Card>
  );
}

export function CourseGrid({ limit }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/courses").then((r) => setItems(r.data)); }, []);
  if (!items) {
    return <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-96" />)}</div>;
  }
  const list = limit ? items.slice(0, limit) : items;
  if (!list.length) return <div className="rounded-xl border border-dashed p-10 text-center text-sm text-muted-foreground">No courses are available right now. Send us an enquiry!</div>;
  return <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{list.map((c) => <CourseCard key={c.id} c={c} />)}</div>;
}

export function FeaturedCourses() {
  return (
    <section id="courses" className="py-20" data-testid="featured-courses">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[hsl(var(--brand-orange))]">Courses</div>
            <h2 className="mt-3 font-display text-3xl font-bold sm:text-4xl">Enroll in the course you need</h2>
            <p className="mt-4 text-base text-muted-foreground">Pay once per course. No subscriptions, no surprises.</p>
          </div>
          <Button asChild variant="outline" data-testid="landing-all-courses"><Link to="/courses">View all courses</Link></Button>
        </div>
        <CourseGrid limit={6} />
      </div>
    </section>
  );
}
